import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { v2MenuItemDangerClass, V2ThemeProvider } from '@onevision-resources/design-theme';
import React from 'react';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { NormalizedSupportPolicy } from 'src/graphql/entities/support-policy/support-policy.utils';
import { POLICY_CATEGORY_ESCALATION } from './policy-constants';

interface SupportPolicyMenuProps {
  hasAccessToManageSupportPolicies: boolean;
  hasAccessToManageEscalationPolicies: boolean;
  menuAnchorEl: HTMLElement | null;
  setMenuAnchorEl: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  menuPolicyId: string | null;
  setMenuPolicyId: React.Dispatch<React.SetStateAction<string | null>>;
  supportPolicies: NormalizedSupportPolicy[];
  handleUnpin: (policy: NormalizedSupportPolicy) => void;
  handlePin: (policy: NormalizedSupportPolicy) => void;
  handleArchive: (policy: NormalizedSupportPolicy) => void;
  handleUnarchive: (policy: NormalizedSupportPolicy) => void;
  handleDelete: (id: string | null) => void;
  onEditPolicy: (policy: NormalizedSupportPolicy) => void;
}

export const SupportPolicyMenu = ({
  hasAccessToManageSupportPolicies,
  hasAccessToManageEscalationPolicies,
  menuAnchorEl,
  setMenuAnchorEl,
  menuPolicyId,
  setMenuPolicyId,
  supportPolicies,
  handleUnpin,
  handlePin,
  handleArchive,
  handleUnarchive,
  handleDelete,
  onEditPolicy,
}: SupportPolicyMenuProps) => {
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuPolicyId(null);
  };

  if (!hasAccessToManageSupportPolicies) return null;

  const policy = menuPolicyId ? supportPolicies.find((p) => p.id === menuPolicyId) : null;
  if (!policy) return null;

  const isEscalation = policy.category === POLICY_CATEGORY_ESCALATION;
  const items: React.ReactNode[] = [];

  if (!policy.archivedAt && (!isEscalation || hasAccessToManageEscalationPolicies)) {
    items.push(
      <MenuItem
        key="edit"
        data-testid="policy-menu-edit"
        onClick={() => {
          onEditPolicy(policy);
          handleMenuClose();
        }}
      >
        <ListItemIcon>
          <DynamicIcon name="pencil" library="lucide" />
        </ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>,
    );
  }

  if (!policy.archivedAt && !isEscalation) {
    if (policy.pinnedAt) {
      items.push(
        <MenuItem key="unpin" onClick={() => handleUnpin(policy)}>
          <ListItemIcon>
            <DynamicIcon name="pin-off" library="lucide" />
          </ListItemIcon>
          <ListItemText>Unpin</ListItemText>
        </MenuItem>,
      );
    } else {
      items.push(
        <MenuItem key="pin" onClick={() => handlePin(policy)}>
          <ListItemIcon>
            <DynamicIcon name="pin" library="lucide" />
          </ListItemIcon>
          <ListItemText>Pin</ListItemText>
        </MenuItem>,
      );
    }
  }

  if (!isEscalation) {
    if (policy.archivedAt) {
      items.push(
        <MenuItem key="unarchive" onClick={() => handleUnarchive(policy)}>
          <ListItemIcon>
            <DynamicIcon name="archive-restore" library="lucide" />
          </ListItemIcon>
          <ListItemText>Unarchive</ListItemText>
        </MenuItem>,
      );
    } else {
      items.push(
        <MenuItem key="archive" onClick={() => handleArchive(policy)}>
          <ListItemIcon>
            <DynamicIcon name="archive" library="lucide" />
          </ListItemIcon>
          <ListItemText>Archive</ListItemText>
        </MenuItem>,
      );
    }

    items.push(
      <MenuItem
        key="delete"
        onClick={() => handleDelete(policy.id)}
        className={v2MenuItemDangerClass}
      >
        <ListItemIcon>
          <DynamicIcon name="trash-2" library="lucide" />
        </ListItemIcon>
        <ListItemText>Delete</ListItemText>
      </MenuItem>,
    );
  }

  return (
    <V2ThemeProvider>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      >
        {items}
      </Menu>
    </V2ThemeProvider>
  );
};
